import { addDays, differenceInDays, format } from "date-fns";
import type { AccommodationDayInfoResponse } from "@/types/api";

export function getValidArrivalDays(
  dayList: AccommodationDayInfoResponse["day_list"],
  nights: number,
): Date[] {
  const valid: Date[] = [];

  Object.entries(dayList).forEach(([arrivalStr, arrivalInfo]) => {
    if (!arrivalInfo.open || arrivalInfo.stock === 0 || !arrivalInfo.can_arrive) return;
    if (arrivalInfo.min_nights !== null && arrivalInfo.min_nights > nights) return;
    if (arrivalInfo.max_nights !== null && arrivalInfo.max_nights < nights) return;

    const arrivalDate = new Date(arrivalStr);

    if (
      arrivalInfo.lead_days !== null &&
      differenceInDays(arrivalDate, new Date()) < arrivalInfo.lead_days
    )
      return;

    // intermediate nights
    for (let i = 1; i < nights; i++) {
      const midInfo = dayList[format(addDays(arrivalDate, i), "yyyy-MM-dd")];
      if (!midInfo || !midInfo.open || midInfo.stock === 0) return;
    }

    // departure
    const deptInfo = dayList[format(addDays(arrivalDate, nights), "yyyy-MM-dd")];
    if (!deptInfo || !deptInfo.open || deptInfo.stock === 0 || !deptInfo.can_departure) return;

    valid.push(arrivalDate);
  });

  return valid;
}

export function computeStayLengthAvailability(
  dayList: AccommodationDayInfoResponse["day_list"],
): Record<number, boolean> {
  const result: Record<number, boolean> = {};
  for (let n = 1; n <= 30; n++) {
    result[n] = getValidArrivalDays(dayList, n).length > 0;
  }
  return result;
}

export function findClosestAvailableNights(
  availability: Record<number, boolean>,
  nights: number,
): number | null {
  if (availability[nights]) return null;
  for (let delta = 1; delta <= 29; delta++) {
    for (const candidate of [nights - delta, nights + delta]) {
      if (candidate >= 1 && candidate <= 30 && availability[candidate]) {
        return candidate;
      }
    }
  }
  return null;
}
